import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PendingJobProvider } from './contexts/PendingJobContext';
import { queryClient } from './lib/queryClient';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import RegularUserRouteGuard from './components/RegularUserRouteGuard';
import ErrorBoundary from './components/ErrorBoundary';
import AdminLayout from './components/admin/AdminLayout';
import AdminRouteGuard from './components/admin/AdminRouteGuard';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const SsoCallbackPage = lazy(() => import('./pages/SsoCallbackPage'));
const SsoErrorPage = lazy(() => import('./pages/SsoErrorPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ContestsPage = lazy(() => import('./pages/ContestsPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const ManageCatalogPage = lazy(() => import('./pages/ManageCatalogPage'));
const HTML5GamesPage = lazy(() => import('./pages/HTML5GamesPage'));
const InstantWinPage = lazy(() => import('./pages/InstantWinPage'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const TriviaQuizzesPage = lazy(() => import('./pages/TriviaQuizzesPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ParticipantsPage = lazy(() => import('./pages/ParticipantsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ContestSettingsPage = lazy(() => import('./pages/ContestSettingsPage'));
const ApiDocumentationPage = lazy(() => import('./pages/ApiDocumentationPage'));
const OrangePuzzleGamePage = lazy(() => import('./pages/OrangePuzzleGamePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const CreateProjectPage = lazy(() => import('./pages/CreateProjectPage'));
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage'));

const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminCustomersPage = lazy(() => import('./pages/admin/AdminCustomersPage'));
const AdminCustomerDetailsPage = lazy(() => import('./pages/admin/AdminCustomerDetailsPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminBillingPage = lazy(() => import('./pages/admin/AdminBillingPage'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));

const SupportPage = lazy(() => import('./pages/SupportPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Set document direction based on language
    const handleLanguageChange = (lng: string) => {
      document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };

    // Set initial direction
    handleLanguageChange(i18n.language);

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <TenantProvider>
              <ImpersonationProvider>
                <ProjectProvider>
                  <PendingJobProvider>
                  <Router>
                    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                      <Suspense fallback={<LoadingFallback />}>
                        <Routes>
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
                          <Route path="/auth/sso/error" element={<SsoErrorPage />} />
                          <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
                          <Route path="/" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <Navigate to="/dashboard" replace />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/dashboard" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <Dashboard />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/contests" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ContestsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/html5-games" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <HTML5GamesPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/instant-win" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <InstantWinPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/challenges" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ChallengesPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/trivia-quizzes" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <TriviaQuizzesPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/rewards" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <RewardsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/manage-catalog" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ManageCatalogPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/users" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <UsersPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/participants" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ParticipantsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/reports" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ReportsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/settings" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <SettingsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/contest-settings" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ContestSettingsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/api-docs" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ApiDocumentationPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/support" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <SupportPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/faq" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <FaqPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/privacy-policy" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <PrivacyPolicyPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/play/orange-puzzle" element={
                            <RegularUserRouteGuard>
                              <OrangePuzzleGamePage />
                            </RegularUserRouteGuard>
                          } />

                          {/* Project Management Routes */}
                          <Route path="/projects" element={
                            <Navigate to="/settings?tab=projects" replace />
                          } />
                          <Route path="/projects/new" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <CreateProjectPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />
                          <Route path="/projects/:projectId/settings" element={
                            <RegularUserRouteGuard>
                              <Layout>
                                <ProjectSettingsPage />
                              </Layout>
                            </RegularUserRouteGuard>
                          } />

                          {/* Admin Panel Routes */}
                          <Route path="/admin" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminOverviewPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/customers" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminCustomersPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/customers/:customerId" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminCustomerDetailsPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/analytics" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminAnalyticsPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/billing" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminBillingPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/support" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminSupportPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />

                          {/* Admin Shared Routes - Accessible to Mega Admins */}
                          <Route path="/admin/users" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <UsersPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />
                          <Route path="/admin/settings" element={
                            <AdminRouteGuard>
                              <AdminLayout>
                                <AdminSettingsPage />
                              </AdminLayout>
                            </AdminRouteGuard>
                          } />

                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </Router>
                  </PendingJobProvider>
                </ProjectProvider>
              </ImpersonationProvider>
            </TenantProvider>
          </AuthProvider>
        </ThemeProvider>
        {import.meta.env.MODE === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;